import React, { useCallback, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import PlantillaScreen from "../../helpers/PlantillaScreen";
import { LoaderScreen } from "../../components/LoaderScreen";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { useBox } from "../../Finanzas/Helper/useBox";
// import FiltroReporteRefaccionesDetalleFamilia from "./FiltroReporteRefaccionesDetalleFamilia";
import { ChartFamiliasRefacciones } from "./ChartFamiliasRefacciones";
import { ChartVentaMensual } from "./ChartVentaMensual";
import { TablaPareto } from "./TablaPareto";
import { TablaKpisPorAnio } from "./TablaKpisPorAnio";
import { KpiCard } from "./KpiCard";
import { FiltroMultiSelect } from "./FiltroMultiSelect";
import { FormatMoney } from "../../../Helpers/FormatNumber";
import { CANTIDAD_MOSTRAR_OPCIONES } from "../../../Arrays/CantidadMostrarArray";
import {
    filtrarDetallePorFamilia,
    filtrarDetalleFamiliaPorFiltros,
    filtrarDetallePorSidebar,
    filtrarDetallePorPartes,
    calcularKpisFamiliaDesdeDetalle,
    calcularKpisFamiliaPorAnio,
    agruparVentasPorClientePlano,
    agruparVentasPorNumeroParteYPeriodo,
    construirSeriesMensualesDesdeDetalle,
    obtenerOpcionesSubFamiliaDesdeDetalle,
    obtenerOpcionesSubFamilia2DesdeDetalle,
    obtenerOpcionesClienteDesdeDetalle,
    obtenerOpcionesAsesorDesdeDetalle,
    obtenerOpcionesSucursalDesdeDetalle,
} from "../../../Helpers/ReporteRefaccionesAgregaciones";

// Pantalla 2 (Detalle por Familia): KPIs, Pareto de Clientes, número de
// parte y dispersión mensual, calculados sobre el `detalle` que reenvía la
// Pantalla 1 al hacer clic en una Familia (ver `onFamiliaClick` en
// ReporteRefaccionesBoceto.js).

const filtroFamiliaVacio = () => ({
    subFamilia: "TODOS",
    subFamilia2: "TODOS",
    cliente: "TODOS",
    asesor: "TODOS",
});

// Formato corto dd/mm/aaaa para mostrar el rango de fechas heredado en el
// encabezado (las fechas llegan en formato ISO yyyy-mm-dd desde FechaInput).
const formatFechaCorta = (isoDate) => {
    if (!isoDate) return "";
    const [anio, mes, dia] = isoDate.split("-");
    return `${dia}/${mes}/${anio}`;
};

const ReporteRefaccionesDetalleFamilia = () => {
    const history = useHistory();
    const { state } = useLocation();

    // Contexto heredado de la Pantalla 1; valores por defecto si se entra directo por URL.
    const hoyIso = new Date().toISOString().split("T")[0];
    const boxstatePadre = state?.boxstate || { fechaInicio: hoyIso, fechaFin: hoyIso };
    const familia = state?.familia || { id: null, nombre: "Familia no especificada" };
    const detalleHeredado = state?.detalle || [];

    const [loader, setLoader] = useState({
        mostrar: false,
        alerta: false,
        error: false,
        mensaje: "",
    });

    const [boxstate, setBoxState] = useBox({
        subFamilia: "TODOS",
        subFamilia2: "TODOS",
        cliente: "TODOS",
        asesor: "TODOS",
    });

    // Filtro propio de esta pantalla; solo se actualiza al presionar "Cargar" (no en vivo).
    const [filtroFamiliaAplicado, setFiltroFamiliaAplicado] = useState(filtroFamiliaVacio);

    const onExecuteFiltroFamilia = (nuevoFiltro) => {
        return new Promise((resolve) => {
            setFiltroFamiliaAplicado(nuevoFiltro);
            resolve(nuevoFiltro);
        });
    };

    // Filtro de Sucursal (toolbar): multi-selección, filtra en memoria al instante.
    const [sidebarFiltros, setSidebarFiltros] = useState({ sucursal: [] });

    const onCambiarFiltroSidebar = (key, nuevaSeleccion) => {
        setSidebarFiltros((prev) => ({ ...prev, [key]: nuevaSeleccion }));
    };

    const onLimpiarFiltrosSidebar = () => setSidebarFiltros({ sucursal: [] });

    // "Venta por número de parte": cantidad y orden a mostrar (el propio
    // ChartFamiliasRefacciones.js ordena/recorta y da scroll).
    const [cantidadNumeroParte, setCantidadNumeroParte] = useState("10");
    const [ordenNumeroParte, setOrdenNumeroParte] = useState("desc");

    // Selección de una sola parte (clic selecciona, clic de nuevo
    // deselecciona; elegir otra reemplaza la anterior) para filtrar
    // dispersión mensual y Pareto; la propia gráfica de número de parte no
    // se autofiltra. useCallback evita reiniciar su canvas de echarts en
    // cada render.
    const [parteSeleccionada, setParteSeleccionada] = useState(null);

    const onNumeroParteClick = useCallback((item) => {
        setParteSeleccionada((prev) => (prev === item.id ? null : item.id));
    }, []);

    const onLimpiarParteSeleccionada = () => setParteSeleccionada(null);

    // Toggle Monto/Cantidad: afecta KPIs, número de parte, dispersión mensual y Pareto.
    const [metricaVista, setMetricaVista] = useState("monto");

    // Año principal (Fecha Fin heredada): acota los KPIs a un solo año.
    const anioPrincipal = boxstatePadre.fechaFin
        ? boxstatePadre.fechaFin.split("-")[0]
        : String(new Date().getFullYear());

    // Detalle acotado a esta Familia, antes del filtro propio (opciones dinámicas + base para filtrar).
    const detalleFamilia = useMemo(
        () => filtrarDetallePorFamilia(detalleHeredado, familia.nombre),
        [detalleHeredado, familia.nombre]
    );


    const opcionesFiltroFamilia = useMemo(
        () => ({
            subFamilia: obtenerOpcionesSubFamiliaDesdeDetalle(detalleFamilia),
            subFamilia2: obtenerOpcionesSubFamilia2DesdeDetalle(detalleFamilia),
            cliente: obtenerOpcionesClienteDesdeDetalle(detalleFamilia),
            asesor: obtenerOpcionesAsesorDesdeDetalle(detalleFamilia),
        }),
        [detalleFamilia]
    );

    // Opciones del filtro de Sucursal, sobre `detalleFamilia` (sin filtrar aún).
    const opcionesSucursal = useMemo(
        () => obtenerOpcionesSucursalDesdeDetalle(detalleFamilia),
        [detalleFamilia]
    );

    // Detalle final: Familia + filtro propio (Cargar) + Sucursal (instantáneo).
    const detalleFiltradoFamilia = useMemo(() => {
        const conFiltroPropio = filtrarDetalleFamiliaPorFiltros(detalleFamilia, filtroFamiliaAplicado);
        return filtrarDetallePorSidebar(conFiltroPropio, sidebarFiltros);
    }, [detalleFamilia, filtroFamiliaAplicado, sidebarFiltros]);

    // `anioPrincipal` acota los KPIs a un solo año (la dispersión mensual usa todos).
    const kpisCalculados = useMemo(
        () => calcularKpisFamiliaDesdeDetalle(detalleFiltradoFamilia, anioPrincipal, metricaVista),
        [detalleFiltradoFamilia, anioPrincipal, metricaVista]
    );


    // Sin filtro de partes: debe seguir mostrando todas para poder seleccionarlas.
    // Desglosado por año (igual forma que ChartFamiliasRefacciones espera).
    const numeroParteDatos = useMemo(
        () => agruparVentasPorNumeroParteYPeriodo(detalleFiltradoFamilia, metricaVista),
        [detalleFiltradoFamilia, metricaVista]
    );

    // Detalle acotado además a la parte seleccionada; alimenta Pareto y dispersión mensual.
    const detalleFiltradoPorPartes = useMemo(
        () => filtrarDetallePorPartes(detalleFiltradoFamilia, parteSeleccionada ? [parteSeleccionada] : []),
        [detalleFiltradoFamilia, parteSeleccionada]
    );

    const clientesParetoDatos = useMemo(
        () => agruparVentasPorClientePlano(detalleFiltradoPorPartes, metricaVista),
        [detalleFiltradoPorPartes, metricaVista]
    );

    // Años de la dispersión mensual: año principal + comparativos seleccionados.
    const aniosMostrados = useMemo(() => {
        const comparativos = (boxstatePadre.anio || []).map(String);
        const conjunto = new Set([...comparativos, anioPrincipal]);
        return [...conjunto].sort((a, b) => Number(a) - Number(b));
    }, [boxstatePadre.anio, anioPrincipal]);

    const seriesMensuales = useMemo(
        () => construirSeriesMensualesDesdeDetalle(detalleFiltradoPorPartes, aniosMostrados, metricaVista),
        [detalleFiltradoPorPartes, aniosMostrados, metricaVista]
    );

    // "KPIs por año" (junto a la dispersión mensual): los 5 KPIs desglosados
    // por año, igual patrón que la Pantalla 3 (Detalle por Cliente). Usa
    // `detalleFiltradoPorPartes` (no `detalleFiltradoFamilia`) para que
    // reaccione a la parte seleccionada en "Venta por número de parte",
    // igual que ya hacen `seriesMensuales`/`clientesParetoDatos`, y respeta
    // `metricaVista` (Monto/Cantidad), igual que el resto de la pantalla.
    // El consolidado (fila final de TablaKpisPorAnio) es el acumulado de
    // TODOS los años (no solo `anioPrincipal`, a diferencia de
    // `kpisCalculados` de arriba).
    const kpisPorAnioFamilia = useMemo(
        () => calcularKpisFamiliaPorAnio(detalleFiltradoPorPartes, aniosMostrados, metricaVista),
        [detalleFiltradoPorPartes, aniosMostrados, metricaVista]
    );

    const kpisConsolidadoFamilia = useMemo(
        () => calcularKpisFamiliaDesdeDetalle(detalleFiltradoPorPartes, undefined, metricaVista),
        [detalleFiltradoPorPartes, metricaVista]
    );


    // Drill-down a Pantalla 3 (360 del Cliente): reenvía `detalleHeredado`
    // completo (todas las Familias), no solo esta Familia.
    const onClienteClick = (fila) => {
        return
        history.push({
            pathname: "/PostVenta/ReporteRefaccionesDetalleCliente",
            state: {
                boxstatePadre,
                familia,
                cliente: { id: fila.id, nombre: fila.nombre },
                detalle: detalleHeredado,
                filtrosReporteRefacciones: state?.filtrosReporteRefacciones,
            },
        });
    };

    // No usa history.goBack() (perdería el estado local de la Pantalla 1); navega reenviando filtros.
    const onRegresar = () => {
        history.push({
            pathname: "/PostVenta/ReporteRefacciones",
            state: { filtrosReporteRefacciones: state?.filtrosReporteRefacciones },
        });
    };

    return (
        <PlantillaScreen
            titulo={`DETALLE FAMILIA: ${familia.nombre.toUpperCase()}`}
            className={"plantilla-detalle-familia"}
            className_main={1}
        >
            <LoaderScreen loader={loader} />
            <div className="peb-actions">
                <div className="peb-actions-regresar" onClick={onRegresar}>
                    <ArrowBackIcon /> Regresar
                </div>
                {/* 
                <div className="peb-actions-filtro">
                    <FiltroReporteRefaccionesDetalleFamilia
                        boxState={boxstate}
                        onBoxChange={setBoxState}
                        onExecute={onExecuteFiltroFamilia}
                        opciones={opcionesFiltroFamilia}
                    />
                </div> */}

                <div className="peb-actions-contexto">
                    Del {formatFechaCorta(boxstatePadre.fechaInicio)} al{" "}
                    {formatFechaCorta(boxstatePadre.fechaFin)}
                </div>
            </div>

            {/* Barra de herramientas: toggle Monto/Cantidad + filtro de Sucursal. */}
            <div className="pdf-toolbar">
                <div className="pdf-metrica-toggle">
                    <button
                        type="button"
                        className={`pdf-metrica-boton${metricaVista === "monto" ? " pdf-metrica-boton--activo" : ""}`}
                        onClick={() => setMetricaVista("monto")}
                    >
                        Monto
                    </button>
                    <button
                        type="button"
                        className={`pdf-metrica-boton${metricaVista === "cantidad" ? " pdf-metrica-boton--activo" : ""}`}
                        onClick={() => setMetricaVista("cantidad")}
                    >
                        Cantidad
                    </button>
                </div>

                <div className="pdf-toolbar-sucursal">
                    <div className="pdf-toolbar-sucursal-campo">
                        <FiltroMultiSelect
                            label="Sucursal"
                            options={opcionesSucursal}
                            selected={sidebarFiltros.sucursal}
                            onChange={(nuevaSeleccion) => onCambiarFiltroSidebar("sucursal", nuevaSeleccion)}
                            flotante
                        />
                    </div>
                    {sidebarFiltros.sucursal.length > 0 && (
                        <div className="pdf-toolbar-sucursal-limpiar" onClick={onLimpiarFiltrosSidebar}>
                            Limpiar
                        </div>
                    )}
                </div>
            </div>

            <div className="pdf-main-content scroll">
                {/* <div className="pdf-kpis">
                    <KpiCard
                        titulo={metricaVista === "cantidad" ? "Cantidad total" : "Venta total"}
                        valor={
                            metricaVista === "cantidad"
                                ? `${Math.round(kpisCalculados.ventaTotal).toLocaleString("es-MX")} unidades`
                                : `$${FormatMoney(kpisCalculados.ventaTotal, 0)}`
                        }
                    />
                    <KpiCard titulo="Facturas" valor={kpisCalculados.facturasTotales.toLocaleString("es-MX")} />
                    <KpiCard
                        titulo={metricaVista === "cantidad" ? "Cantidad por factura" : "Factura promedio"}
                        valor={
                            metricaVista === "cantidad"
                                ? `${Math.round(kpisCalculados.facturaPromedio).toLocaleString("es-MX")} unidades`
                                : `$${FormatMoney(kpisCalculados.facturaPromedio, 0)}`
                        }
                    />
                     Margen: (importe - costo) / importe. No cambia con el toggle Monto/Cantidad.
                    <KpiCard titulo="Margen" valor={`${(kpisCalculados.margen * 100).toFixed(1)}%`} />
                </div> */}

                <div className="pdf-mensual">
                    <div className="pdf-mensual-chart-container">
                        <div className="pdf-mensual-title">
                            Dispersión de {metricaVista === "cantidad" ? "cantidad" : "venta"} mensual
                            {aniosMostrados.length > 1 ? " (comparativo por año)" : ""}
                            {parteSeleccionada ? ` — filtrado por la parte ${parteSeleccionada}` : ""}
                        </div>
                        {/* Wrapper flex necesario para que el chart no tome el 100% del contenedor, dejando espacio al título. */}
                        <div className="pdf-mensual-chart-wrapper">
                            <ChartVentaMensual series={seriesMensuales} metrica={metricaVista} />
                        </div>
                    </div>

                    {/* KPIs por año: mismo componente/patrón que Pantalla 3 (Detalle por Cliente). */}
                    <div className="pdf-mensual-kpis-container">
                        <div className="pdf-mensual-title">
                            KPIs por año
                            {parteSeleccionada ? ` — filtrado por la parte ${parteSeleccionada}` : ""}
                        </div>
                        <TablaKpisPorAnio
                            filas={kpisPorAnioFamilia}
                            anioPrincipal={anioPrincipal}
                            consolidado={kpisConsolidadoFamilia}
                            metrica={metricaVista}
                        />
                    </div>
                </div>

                <div className="pdf-body">
                    <div className="pdf-body-chart">
                        <div className="pdf-body-chart-container">
                            <div className="pdf-numparte-header">
                                <div className="pdf-body-title">
                                    {metricaVista === "cantidad" ? "Cantidad por número de parte" : "Venta por número de parte"}
                                </div>
                                <div className="pdf-numparte-controles">
                                    <select
                                        className="pdf-numparte-cantidad"
                                        value={cantidadNumeroParte}
                                        onChange={({ target }) => setCantidadNumeroParte(target.value)}
                                    >
                                        {CANTIDAD_MOSTRAR_OPCIONES.map((opcion) => (
                                            <option key={opcion} value={opcion}>
                                                {opcion === "todos" ? "Todos" : opcion}
                                            </option>
                                        ))}
                                    </select>
                                    <div
                                        className="pdf-numparte-orden"
                                        title={
                                            ordenNumeroParte === "desc"
                                                ? "Mostrando de mayor a menor. Clic para invertir."
                                                : "Mostrando de menor a mayor. Clic para invertir."
                                        }
                                        onClick={() =>
                                            setOrdenNumeroParte((prev) => (prev === "desc" ? "asc" : "desc"))
                                        }
                                    >
                                        {ordenNumeroParte === "desc" ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
                                    </div>
                                </div>
                            </div>

                            {/* Solo visible con una parte seleccionada; dispersión mensual y Pareto ya la reflejan. */}
                            {parteSeleccionada && (
                                <div className="pdf-numparte-seleccion">
                                    <span>Parte seleccionada: {parteSeleccionada}</span>
                                    <span
                                        className="pdf-numparte-seleccion-limpiar"
                                        onClick={onLimpiarParteSeleccionada}
                                    >
                                        Limpiar
                                    </span>
                                </div>
                            )}

                            <ChartFamiliasRefacciones
                                id="chart-numero-parte-refacciones"
                                datos={numeroParteDatos}
                                anios={aniosMostrados}
                                anioPrincipal={anioPrincipal}
                                cantidad={cantidadNumeroParte}
                                orden={ordenNumeroParte}
                                onItemClick={onNumeroParteClick}
                                seleccionados={parteSeleccionada ? [parteSeleccionada] : []}
                                metrica={metricaVista}
                            />
                        </div>
                    </div>
                    <div className="pdf-body-pareto">
                        <TablaPareto
                            titulo={
                                parteSeleccionada
                                    ? `Pareto de Clientes (80/20) — filtrado por la parte ${parteSeleccionada}`
                                    : "Pareto de Clientes (80/20)"
                            }
                            columnaNombre="Cliente"
                            datos={clientesParetoDatos}
                            onRowClick={onClienteClick}
                            metrica={metricaVista}
                        />
                    </div>
                </div>
            </div>
        </PlantillaScreen>
    );
};

export default ReporteRefaccionesDetalleFamilia;
